import React from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Customer } from '@api/Customers/Customer';
import { PromotionalCode } from '@api/PromotionalCodes/PromotionalCode';
import { SalesChannel } from '@api/SalesChannels/SalesChannel';
import { SavedOrder } from '@api/SavedOrders/SavedOrder';
import { Dish } from '@api/Dishes/Dish';
import { Recipe } from '@api/Recipes/Recipe';
import { ModifierGroup } from '@api/ModifierGroups/ModifierGroup';
import { Input } from '@api/Inputs/Input';
import { ShoppingCartLineV2 } from '@api/ShoppingCart/ShoppingCartV2';

import { ModalDishInfo } from '@screens/POS/components/ModalDishInfo';
import { ModalGetCustomer } from '@components/Modals/ModalGetCustomer';
import { ModalAddPromotionalCode } from '@components/Collaborator/Modals/ModalAddPromotionalCode';
import { ModalAddShippingCost } from '@components/Collaborator/Modals/ModalAddShippingCost';
import { ModalAddOrderNote } from '@components/Collaborator/Modals/ModalAddOrderNote';
import { ModalPago } from '@components/Collaborator/Modals/ModalPago';
import { ModalOpenCashRegister } from '@components/Collaborator/Modals/ModalOpenCashRegister';
import { ModalShowTicket } from '@components/Collaborator/Modals/ModalShowTicket';
import { TicketImageProps } from '@components/Collaborator/Modals/ModalShowTicket/TicketImage';
import { ModalListSavedOrders } from '@components/Collaborator/Modals/ModalListSavedOrders';

/**
 * Los 9 modales del POS, sacados del render de `POSScreen` (F4 del
 * PLAN-REDISENO-PANEL-POS-Y-RECIBO). Es un contenedor SIN lógica: cada prop
 * viaja tal cual al modal que le toca y conserva EL MISMO NOMBRE que tenía en
 * la pantalla, a propósito — los 9 hooks son del mismo tipo `OpenModalHookType`
 * y TypeScript no podría avisar si se cruzaran dos.
 */
interface PosModalsProps {
    // --- Personalizar producto ---
    modalDishInfo: OpenModalHookType;
    currentDish?: Dish;
    editingLine?: ShoppingCartLineV2;
    modifierGroupsById: Map<string, ModifierGroup>;
    recipesById: Map<string, Recipe>;
    inputsById: Map<string, Input>;
    unavailableRecipeIds: Set<string>;
    onAddLine: (line: ShoppingCartLineV2) => void;

    // --- Cliente ---
    modalGetCustomer: OpenModalHookType;
    onAddCustomer: (customer: Customer) => void;
    identifyPrefillPhone?: string;
    onRequestIdentifyOnScreen: () => void;

    // --- Promoción, envío y nota ---
    modalAddPromotionalCode: OpenModalHookType;
    onAddPromotionalCode: (promotionalCode: PromotionalCode) => void;
    modalAddShippingCost: OpenModalHookType;
    onAddShippingCost: (amount: number) => void;
    modalAddOrderNote: OpenModalHookType;
    onAddOrderNote: (note: string) => void;

    // --- Pago ---
    modalPago: OpenModalHookType;
    pagoRemaining: number;
    salesChannel?: SalesChannel;
    onRegisterPayment: (type: 'cash' | 'card' | 'transfer', amount: number) => void;
    onContinuePago: () => void;

    // --- Caja ---
    modalOpenCashRegister: OpenModalHookType;
    onOpenCashRegister: (amount: number) => void;
    cashRegisterMandatory: boolean;
    /** Sucursal en operación: define qué cajas rezagadas bloquean la apertura. */
    cashRegisterBranchOfficeId?: string;
    onExitCashRegister: () => void;

    // --- Pedidos guardados ---
    modalListSavedOrders: OpenModalHookType;
    onTakeSavedOrder: (order: SavedOrder) => void;

    // --- Recibo (el lienzo completo, tal como lo arma la pantalla) ---
    modalShowTicket: OpenModalHookType;
    ticket: TicketImageProps;
}

export const PosModals: React.FunctionComponent<PosModalsProps> = (props) => {
    const {
        modalDishInfo,
        currentDish,
        editingLine,
        modifierGroupsById,
        recipesById,
        inputsById,
        unavailableRecipeIds,
        onAddLine,
        modalGetCustomer,
        onAddCustomer,
        identifyPrefillPhone,
        onRequestIdentifyOnScreen,
        modalAddPromotionalCode,
        onAddPromotionalCode,
        modalAddShippingCost,
        onAddShippingCost,
        modalAddOrderNote,
        onAddOrderNote,
        modalPago,
        pagoRemaining,
        salesChannel,
        onRegisterPayment,
        onContinuePago,
        modalOpenCashRegister,
        onOpenCashRegister,
        cashRegisterMandatory,
        cashRegisterBranchOfficeId,
        onExitCashRegister,
        modalListSavedOrders,
        onTakeSavedOrder,
        modalShowTicket,
        ticket
    } = props;

    return (
        <>
            <ModalDishInfo
                modalDishInfo={modalDishInfo}
                dish={currentDish}
                editingLine={editingLine}
                modifierGroupsById={modifierGroupsById}
                recipesById={recipesById}
                inputsById={inputsById}
                unavailableRecipeIds={unavailableRecipeIds}
                onAddLine={onAddLine}
            />
            <ModalGetCustomer
                modalGetCustomer={modalGetCustomer}
                onSelectCustomer={onAddCustomer}
                showSkip
                prefillPhone={identifyPrefillPhone}
                onRequestOnScreen={onRequestIdentifyOnScreen}
            />
            <ModalAddPromotionalCode
                modalAddPromotionalCode={modalAddPromotionalCode}
                onAddPromotionalCode={onAddPromotionalCode}
            />
            <ModalAddShippingCost modalAddShippingCost={modalAddShippingCost} onAddShippingCost={onAddShippingCost} />
            <ModalAddOrderNote modalAddOrderNote={modalAddOrderNote} onAddOrderNote={onAddOrderNote} />
            <ModalPago
                modalPago={modalPago}
                remaining={pagoRemaining}
                salesChannel={salesChannel}
                onRegisterPayment={onRegisterPayment}
                onContinue={onContinuePago}
            />
            <ModalOpenCashRegister
                modalOpenCashRegister={modalOpenCashRegister}
                onOpenCashRegister={onOpenCashRegister}
                // Sin caja no se opera el POS: el modal no se puede descartar
                mandatory={cashRegisterMandatory}
                onExit={onExitCashRegister}
                // Y desde el 2026-09-10 tampoco se puede abrir la de hoy si
                // quedó una sin cerrar de otro día en esta sucursal.
                branchOfficeId={cashRegisterBranchOfficeId}
            />
            <ModalListSavedOrders modalListSavedOrders={modalListSavedOrders} onTakeOrder={onTakeSavedOrder} />
            {/* Recibo PRE-COBRO: sin folio (todavía no se quema) → el lienzo lo
                pinta como "Confirma tu pedido", que es para lo que el negocio lo
                usa cuando el cliente pide por WhatsApp */}
            <ModalShowTicket modalShowTicket={modalShowTicket} {...ticket} />
        </>
    );
};
